import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Callout,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tabs,
  Tag,
} from "@diametral/design-system/react";

import { useProject } from "../../api/projects";
import { useRequests } from "../../api/requests";
import type { ProjectDetail, ProjectStatus, Stream, StreamMember } from "../../api/types";
import { MyStatusTag, ProjectStatusTag } from "../../components/Badges";
import { ButtonLink } from "../../components/ButtonLink";
import { Page, useDocumentTitle } from "../../components/Page";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../../components/States";
import { TrlMeter } from "../../components/TrlMeter";
import { ApiError } from "../../lib/api";
import { useCurrentUser } from "../../lib/currentUser";
import { t, useTranslation } from "../../lib/i18n";
import { AssignDialog, EndAssignmentDialog, RolesDialog, StatusDialog } from "./DarDialogs";
import { EditProjectSheet } from "./EditProjectSheet";
import { HistoryTab } from "./HistoryTab";
import { OverviewTab } from "./OverviewTab";
import { JoinDialog, LeaveDialog } from "./RequestDialogs";
import { ReportsTab } from "./ReportsTab";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "reports", label: "Reports" },
  { id: "history", label: "History" },
];

export interface MembershipView {
  /** Streams the person is assigned to, current or upcoming. */
  myStreams: Stream[];
  /** Active streams the person could still ask to join. */
  joinable: Stream[];
  joinBlocked: string | null;
  leaveBlocked: string | null;
}

/** What the signed-in person may ask on this project, and why not when they may not. */
export function membershipView(
  project: ProjectDetail,
  person: { id: number; assignable: boolean } | null,
  pendingKinds: Set<string>,
): MembershipView {
  const myStreams = person ? project.streams.filter((s) => s.members.some((m) => m.id === person.id)) : [];
  const mine = new Set(myStreams.map((s) => s.id));
  const joinable = project.streams.filter((s) => !s.archived && !mine.has(s.id));
  let joinBlocked: string | null = null;
  if (!person) joinBlocked = t("Your account is not linked to any Boond person.");
  else if (project.status === "paused") joinBlocked = t("This project is paused: it does not accept new requests.");
  else if (project.status === "closed") joinBlocked = t("This project is closed.");
  else if (!person.assignable) joinBlocked = t("You are not in the assignable population: contact the DAR.");
  else if (pendingKinds.has("join")) joinBlocked = t("You already have a pending request to join this project.");
  else if (!joinable.length) joinBlocked = t("You are already assigned to every active stream of this project.");
  const leaveBlocked = pendingKinds.has("leave") ? t("You already have a pending request to leave this project.") : null;
  return { myStreams, joinable, joinBlocked, leaveBlocked };
}

function ProjectBreadcrumb({ title }: { title: string }) {
  const { t } = useTranslation();
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink render={<Link to="/projects" />}>{t("Projects")}</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{title}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}

export function ProjectDetailPage() {
  const { t } = useTranslation();
  const { slug = "" } = useParams();
  const project = useProject(slug);
  useDocumentTitle(project.data?.title ?? t("Project"));

  if (project.isPending) {
    return (
      <div className="ada-page">
        <LoadingBlock lines={8} label={t("Loading project…")} />
      </div>
    );
  }
  if (project.isError) {
    const notFound = project.error instanceof ApiError && project.error.status === 404;
    return (
      <div className="ada-page">
        {notFound ? (
          <EmptyBlock icon="folder" title={t("Project not found")} description={t("No project answers to the address “{{slug}}”.", { slug })}>
            <ButtonLink variant="primary" to="/projects">
              {t("Back to projects")}
            </ButtonLink>
          </EmptyBlock>
        ) : (
          <ErrorBlock error={project.error} onRetry={() => void project.refetch()} />
        )}
      </div>
    );
  }
  return <ProjectView project={project.data} />;
}

function ProjectView({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((item) => item.id === params.get("tab")) ? (params.get("tab") as string) : "overview";
  const [dialog, setDialog] = useState<"join" | "leave" | "edit" | "roles" | "assign" | null>(null);
  const [statusTarget, setStatusTarget] = useState<ProjectStatus | null>(null);
  const [ending, setEnding] = useState<{ member: StreamMember; stream: Stream } | null>(null);

  const myRequests = useRequests({ mine: true }, me.linked);
  const pendingKinds = useMemo(
    () =>
      new Set(
        (myRequests.data ?? []).filter((r) => r.project === project.slug && r.status === "pending").map((r) => r.kind),
      ),
    [myRequests.data, project.slug],
  );
  const person = me.person ? { id: me.person.id, assignable: me.person.assignable } : null;
  const view = membershipView(project, person, pendingKinds);
  const isMember = view.myStreams.length > 0;
  const manage = project.can_manage && project.status !== "closed";

  function selectTab(id: string) {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (id === "overview") next.delete("tab");
        else next.set("tab", id);
        return next;
      },
      { replace: true },
    );
  }

  const actions = (
    <>
      {person && (!isMember || view.joinable.length > 0) ? (
        <Button
          variant={isMember ? "outline" : "primary"}
          disabled={Boolean(view.joinBlocked)}
          title={view.joinBlocked ?? undefined}
          onClick={() => setDialog("join")}
        >
          {t("Ask to join")}
        </Button>
      ) : null}
      {person && isMember ? (
        <Button
          variant="outline"
          disabled={Boolean(view.leaveBlocked)}
          title={view.leaveBlocked ?? undefined}
          onClick={() => setDialog("leave")}
        >
          {t("Ask to leave")}
        </Button>
      ) : null}
      {project.can_edit ? (
        <Button variant="outline" onClick={() => setDialog("edit")}>
          {t("Edit")}
        </Button>
      ) : null}
      {manage ? (
        <DropdownMenu>
          <DropdownMenuTrigger render={<Button variant="outline" />}>{t("Manage (DAR)")}</DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setDialog("assign")}>{t("Assign a person…")}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDialog("roles")}>{t("Appoint the TL/PO…")}</DropdownMenuItem>
            <DropdownMenuSeparator />
            {project.status === "active" ? (
              <DropdownMenuItem onClick={() => setStatusTarget("paused")}>{t("Pause…")}</DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => setStatusTarget("active")}>{t("Reactivate…")}</DropdownMenuItem>
            )}
            <DropdownMenuItem variant="destructive" onClick={() => setStatusTarget("closed")}>
              {t("Close the project…")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </>
  );

  return (
    <Page
      kicker={t("Project")}
      title={project.title}
      breadcrumb={<ProjectBreadcrumb title={project.title} />}
      meta={
        <div className="ada-cluster">
          <ProjectStatusTag status={project.status} />
          <MyStatusTag status={project.my_status} />
          <TrlMeter trl={project.trl} label={project.trl_label} compact />
          {project.technologies.map((tech) => (
            <Tag key={tech}>{tech}</Tag>
          ))}
        </div>
      }
      actions={actions}
      tabs={<Tabs items={TABS.map((item) => ({ ...item, label: t(item.label) }))} value={tab} onChange={selectTab} />}
    >
      {person && !isMember && view.joinBlocked && project.status !== "closed" ? (
        <Callout type="info">{view.joinBlocked}</Callout>
      ) : null}
      {project.status === "closed" ? (
        <Callout type="warning">{t("This project is closed: it can still be viewed but no longer edited.")}</Callout>
      ) : null}

      {tab === "overview" ? (
        <OverviewTab project={project} myPersonId={person?.id ?? null} onEndAssignment={(member, stream) => setEnding({ member, stream })} />
      ) : tab === "reports" ? (
        <ReportsTab project={project} />
      ) : (
        <HistoryTab project={project} />
      )}

      <JoinDialog
        open={dialog === "join"}
        onOpenChange={(open) => setDialog(open ? "join" : null)}
        project={project}
        streams={view.joinable}
        blockedReason={view.joinBlocked}
      />
      <LeaveDialog
        open={dialog === "leave"}
        onOpenChange={(open) => setDialog(open ? "leave" : null)}
        project={project}
        myStreams={view.myStreams}
        blockedReason={view.leaveBlocked}
      />
      {project.can_edit ? (
        <EditProjectSheet open={dialog === "edit"} onOpenChange={(open) => setDialog(open ? "edit" : null)} project={project} isDar={me.is_dar} />
      ) : null}
      {manage ? (
        <>
          <RolesDialog open={dialog === "roles"} onOpenChange={(open) => setDialog(open ? "roles" : null)} project={project} />
          <AssignDialog open={dialog === "assign"} onOpenChange={(open) => setDialog(open ? "assign" : null)} project={project} />
          <StatusDialog target={statusTarget} onClose={() => setStatusTarget(null)} project={project} />
        </>
      ) : null}
      {project.can_manage ? <EndAssignmentDialog target={ending} onClose={() => setEnding(null)} /> : null}
    </Page>
  );
}
